import { useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { FaWater } from "react-icons/fa";
import { FaDollarSign, FaUser, FaLocationDot, FaFire, FaShieldHalved, FaFileLines, FaUpRightFromSquare } from "react-icons/fa6";
import { PiMountains, PiQuestionBold } from "react-icons/pi";
import { BsTree } from "react-icons/bs";
import { VscCompass } from "react-icons/vsc";
import "./WhyForgeExists.css";
import CERRO_TUSA from "../utils/location";
import "./About.css";
import { Container } from "react-bootstrap";
import CircularBadge from "../badge";

/* ── Animation helpers ──────────────────────────────────────── */
const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease, delay } },
});
const fadeIn = (delay = 0) => ({
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.9, ease, delay } },
});

const RAIL_ICONS = [PiMountains, BsTree, FaWater, VscCompass];
const WORDS = ["PURPOSE", "BROTHERHOOD", "HEALING", "TRANSFORMATION"];

/* ── FAQ — grounded in confirmed site policy, not invented ──── */
const FAQS = [
  {
    Icon: FaDollarSign,
    q: "Is there a cost to attend a Forge retreat?",
    a: "No. Forge provides transformational retreat experiences at no cost to eligible participants — that's part of why we're a registered 501(c)(3) public charity.",
  },
  {
    Icon: FaUser,
    q: "Who is eligible to apply?",
    a: "Veterans, first responders, law enforcement, and Gold Star families. During registration we ask for one piece of official documentation confirming service or eligibility — a DD-214, an agency letter, a certification, or similar.",
  },
  {
    Icon: FaLocationDot,
    q: "Where do retreats take place?",
    a: "At our partnered 32-acre eco-luxury resort in Colombia. Medical staff are on site for the entire retreat.",
  },
  {
    Icon: FaFire,
    q: "What does the retreat itself involve?",
    a: "Depending on your path, an ayahuasca ceremony, a mushroom ceremony, or an abstinence track for those not doing medicine — each followed by recovery time, group and individual sessions, and support to help you process the experience.",
  },
  {
    Icon: FaShieldHalved,
    q: "Is Forge a registered nonprofit?",
    a: "Yes. Forge NGO has been a registered 501(c)(3) public charity since December 2024, EIN 33-2406117.",
    link: { label: "View our IRS determination letter", href: "/documents/irs-determination-letter.pdf" },
  },
];

function FaqItem({ item, open, onToggle }) {
  const { Icon } = item;
  return (
    <div className={`about-faq__item ${open ? "about-faq__item--open" : ""}`}>
      <button type="button" className="about-faq__q" onClick={onToggle} aria-expanded={open}>
        <span className="about-faq__q-icon">
          <Icon aria-hidden="true" />
        </span>
        <span className="about-faq__q-text">{item.q}</span>
        <span className="about-faq__toggle" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease }} className="about-faq__a-wrap">
            <p className="about-faq__a">{item.a}</p>
            {item.link && (
              <a href={item.link.href} target="_blank" rel="noreferrer noopener" className="about-faq__link">
                <FaFileLines aria-hidden="true" />
                {item.link.label}
                <FaUpRightFromSquare aria-hidden="true" className="about-faq__link-out" />
              </a>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Coordinate stamp (unchanged) ───────────────────────────── */
function CoordStamp() {
  return (
    <div className="wfe__stamp sad" aria-label="Forge Retreat coordinates, Colombia">
      <div className="stamp_inner">
        <div className="wfe__stamp-header">
          <span className="wfe__stamp-name">FORGE</span>
          <img src="./assets/logo.png" alt="" className="wfe__stamp-logo" aria-hidden="true" />
        </div>
        <div className="wfe__stamp-row">
          <span className="wfe__stamp-label">RETREAT</span>
          <span className="wfe__stamp-value">COLOMBIA</span>
        </div>
        <div className="wfe__stamp-row">
          <span className="wfe__stamp-label">COORDINATES</span>
        </div>
        <div className="wfe__stamp-coords">{CERRO_TUSA.display}</div>
        <div className="wfe__stamp-divider" aria-hidden="true" />
        <div className="wfe__stamp-meta">
          <span>ELEV. 1,850 M</span>
          <span>RETREAT 212</span>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────── */
export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [openFaq, setOpenFaq] = useState(0);

  const photoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: photoRef, offset: ["start end", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section className="wfe about-sec" id="about" ref={ref}>
      {/* ── Why Forge Exists ── */}
      <Container fluid="xl" className="wfe__layout container-2xxl">
        <motion.aside className="hero__rail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5, ease }} aria-hidden="true">
          <span className="hero__rail-num">05</span>
          <span className="hero__rail-line" />
          <ul className="hero__rail-icons">
            {RAIL_ICONS.map((Icon, i) => (
              <li key={i}>
                <Icon />
                <span className={`hero__rail-line ${i === RAIL_ICONS.length - 1 ? "" : "alt"}`} />
              </li>
            ))}
          </ul>
        </motion.aside>

        <div className="wfe__left">
          <motion.div className="wfe__label" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <span className="u-eyebrow">About Forge</span>
          </motion.div>

          <motion.h2 className="wfe__headline" variants={fadeUp(0.08)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <span className="wfe__hl-ink u-display">
              The System Kept
              <br />
              Treating Symptoms.
            </span>
            <span className="wfe__hl-rust u-display">
              We Wanted to
              <br />
              Treat People.
            </span>
          </motion.h2>

          <motion.div className="wfe__body" variants={fadeUp(0.16)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <p>Forge was built by veterans, first responders, and medical professionals who refused to accept a broken system as the only option.</p>
            <p>We've watched too many of our brothers and sisters get left behind. We built Forge as a different kind of option—one that treats the whole person and gets to the root.</p>
            <p>We've watched people who didn't want to be alive find a reason to keep going.</p>
          </motion.div>

          <motion.div className="wfe__right d-md-none d-block" ref={photoRef} variants={fadeIn(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <div className="wfe__photo-wrap">
              <motion.img src="./assets/founders/shane.png" alt="Shane Yost, Founder of Forge, with fellow founders" className="wfe__photo" style={{ y: photoY }} />
            </div>
            <motion.div className="wfe__stamp-wrap" variants={fadeUp(0.45)} initial="hidden" animate={inView ? "show" : "hidden"}>
              <CoordStamp />
            </motion.div>
          </motion.div>

          <motion.div className="wfe__sig-row" variants={fadeUp(0.24)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <CircularBadge colors={"var(--c-forest)"} isForge={false} />
            <div className="wfe__sig-block">
              <span className="wfe__sig-script" aria-hidden="true">
                Shane Yost
              </span>
              <span className="wfe__sig-name">Shane Yost&nbsp;|&nbsp;Co-Founder</span>
            </div>
          </motion.div>
        </div>

        <motion.div className="wfe__right d-md-block d-none" ref={photoRef} variants={fadeIn(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <div className="wfe__photo-wrap">
            <motion.img src="./assets/founders/shane.png" alt="Shane Yost, Co-Founder of Forge, with fellow founders" className="wfe__photo" style={{ y: photoY }} />
          </div>
          <motion.div className="wfe__stamp-wrap" variants={fadeUp(0.45)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <CoordStamp />
          </motion.div>
        </motion.div>
      </Container>

      <div className="wfe__strip" aria-label="Purpose, Brotherhood, Healing, Transformation">
        {WORDS.map((w, i) => (
          <span key={w} className="wfe__strip-item">
            {w}
            {i < WORDS.length - 1 && (
              <span className="wfe__strip-dot" aria-hidden="true">
                •
              </span>
            )}
          </span>
        ))}
      </div>

      {/* ── The People Behind Forge ── */}
      <div className="about-team container-fg container-2xxl">
        <motion.div className="about-team__eyebrow-row" variants={fadeUp(0)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
          <span className="u-eyebrow about-team__eyebrow">The People Behind Forge</span>
          <span className="about-team__rule" aria-hidden="true">
            <PiMountains size={24} color="var(--text-ink)" />
          </span>
        </motion.div>
        <motion.div className="about-team__grid" variants={fadeUp(0.06)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
          <div className="about-team__card">
            <img src="./assets/founders/shane.png" alt="Shane Yost" className="about-team__photo" />
            <span className="about-team__role">Co-Founder</span>
            <p className="about-team__name">Shane Yost</p>
            <span className="about-team__dash" aria-hidden="true" />
            <p className="about-team__bio">
              Combat veteran, first responder, and men's health advocate. Shane founded Forge to create a path to healing that really works—one that gets to the root and restores purpose.
            </p>
            <div className="about-team__stamp">
              <CircularBadge colors="var(--c-forest)" isForge={false} />
            </div>
          </div>
          <div className="about-team__card">
            <img src="./assets/founders/joshua-bourque.jpeg" alt="Joshua Bourque" className="about-team__photo" />
            <span className="about-team__role">Co-Founder</span>
            <p className="about-team__name">Joshua Bourque</p>
            <span className="about-team__dash" aria-hidden="true" />
            <p className="about-team__bio">Combat veteran and men's health strategist. Josh drives Forge's programs and partnerships to expand access and create lasting transformation.</p>
            <div className="about-team__stamp">
              <CircularBadge colors="var(--c-rust)" icon={PiMountains} />
            </div>
          </div>
          {/* <div className="about-team__card about-team__card--pending">
            <span className="about-team__pending-icon">
              <FaUserGroup aria-hidden="true" />
            </span>
            <p className="about-team__pending-label">More of the team, coming soon</p>
            <p className="about-team__pending-sub">Bios for the rest of the clinical, research, and operations team are in progress.</p>
          </div> */}
        </motion.div>
      </div>

      {/* ── FAQ ── */}
      <div className="about-faq-wrap container-fg container-2xxl">
        <div className="about-faq">
          <motion.div className="about-faq__eyebrow-row mb-5" variants={fadeUp(0)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            <span className="about-faq__dots" aria-hidden="true">
              — <PiQuestionBold size={24} color="var(--c-rust)" style={{ transform: "scaleX(-1)" }} />
            </span>

            <span className="u-eyebrow about-faq__eyebrow">Frequently Asked Questions</span>
            <span className="about-faq__dots" aria-hidden="true">
              <PiQuestionBold size={24} color="var(--c-rust)" /> —
            </span>
          </motion.div>
          <div className="about-faq__list">
            {FAQS.map((item, i) => (
              <FaqItem key={item.q} item={item} open={openFaq === i} onToggle={() => setOpenFaq((cur) => (cur === i ? null : i))} />
            ))}
          </div>
        </div>
      </div>

      <div className="about-mountain-band" aria-hidden="true">
        <img src="./assets/mountainsForst.png" alt="" className="mx-auto" />
      </div>
    </section>
  );
}
